import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import axios from 'redaxios'
import { config } from '../../../constants'
import { Callback } from '../../../modules/callback'
import { IFlowCore, IFlowSubscriber } from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import { ConsoleLog } from '../../../modules/logging'
import MainModule from '../../../modules/main'
import { getCardPlaceholder, isObjectEmpty } from '../../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'lvc-create-event-representations', {
  title: 'Représentations',
  titleFestival: 'Représentations et/ou expositions',
  description:
    "Veuillez ajouter une ou des représentations à l'événement. Une représentation doit être créée pour chaque date ou plage horaire à laquelle il y aura une ou des prestations.",
  descriptionFestival:
    "Veuillez ajouter une ou des représentations ou expositions au festival. Si vous ne retrouvez pas votre événement ou exposition, vous devez d'abord aller les créer.",
  existingRepresentationsLabel: 'Représentations existantes',
  existingRepresentationsAndExhibitionsLabel: 'Représentations et expositions existantes',
  chooseRepresentationsLabel: 'Représentations',
  noExistingRepresentationsLabel:
    'Il n’existe aucune représentation associée à ce spectacle actuellement.',
  addedRepresentationsLabel: 'Représentations ajoutées',
  addedRepresentationsAndExhibitionsLabel: 'Représentations et expositions ajoutées',
  addRepresentation: 'Ajouter une autre représentation',
  noOffersEventError: "Cet événement n'a pas d'offres actives, veuillez en choisir un autre",
  noSelectedOffersError: 'Veuillez ajouter au moins un événement ou une représentation',
  nameOfEventOrExhibition: "Nom de l'événement ou de l'exposition",
})

Locale.getInstance().registerKeySet('en', 'lvc-create-event-representations', {
  title: 'Representations',
  titleFestival: 'Showings and/or Exhibitions',
  description:
    'Please add one or more performances to the event. A performance must be created for each date or time slot on which there will be one or more performances.',
  descriptionFestival:
    "Please add one or more showings or exhibitions to the festival. If you can't find your representation or exhibition, you must first create it.",
  existingRepresentationsLabel: 'Existing representations',
  existingRepresentationsAndExhibitionsLabel: 'Existing shows and exhibitions',
  chooseRepresentationsLabel: 'Showings',
  noExistingRepresentationsLabel: 'There are no performances currently associated with this show.',
  addedRepresentationsLabel: 'Added representations',
  addedRepresentationsAndExhibitionsLabel: 'Showings and exhibitions added',
  addRepresentation: 'Add another representation',
  noOffersEventError: 'This event has no active offers, please choose another one',
  noSelectedOffersError: 'Please add at least one event or representation',
  nameOfEventOrExhibition: 'Search for a showing or exhibition',
})

export interface LvcCreateEventRepresentations {
  flowGroup: string
  flowStep: string
  eventName: string
  eventImage: string
  eventDescription: string
  eventDisciplines: string
  existingRepresentations: any[]
  representations: any[]
  modifyingExistingEvent: boolean
}

export interface LvcCreateEventRepresentationsData {
  eventName: string
  eventImage: string
  eventDescription: string
  eventDisciplines: string
  representations: any[]
}

export class LvcCreateEventRepresentations
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  private onEventChange: string = ''
  private core: IFlowCore | undefined
  attrs: Attrs = [
    ['flow-step', ''],
    ['data-type', 'event'],
    ['flow-group', ''],
  ]

  reactive: Reactive = [
    ['eventImage', ''],
    ['eventImagePath', ''],
    ['eventName', ''],
    ['eventDescription', ''],
    ['eventDisciplines', ''],
    ['eventFinalRecapData', {}],
    ['existingRepresentations', []],
    ['representations', []],
    ['modifyingExistingEvent', false],
  ]

  handleModalEvent = (event: any) => {
    this.eventName = event.detail
    ConsoleLog(true, this.eventName)
  }

  async onChangeEvent(data: any) {
    const id = data[0].id
    const schema = data[0].table

    try {
      if (schema === 'events') {
        const result: any = await axios.get(
          `${config.backendApi}/content/getOffersForEventV2?id=${id}&schema=events`
        )
        if (!result.data?.length) {
          const mainModule = MainModule.getInstance()
          mainModule.showToastAlert(
            Locale.getKey('lvc-create-event-reprewsentations.noOffersEventError'),
            3000,
            true
          )
          return
        }
        this.core?.setStepData('addedEventConfirmation', {
          data: data[0],
          offers: result.data,
        })
      } else {
        this.core?.setStepData('addedEventConfirmation', { data: data[0] })
      }

      this.core?.goTo('addedEventConfirmation')
    } catch (error) {
      console.log(error)
    }
  }

  async onReady(): Promise<void> {
    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)

    ConsoleLog(true, 'this.flowGroup : ', this.flowGroup)
    this.core = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)
    this.onEventChange = Callback.attach(this.onChangeEvent.bind(this))
    this.rerender()
  }

  onDestroy() {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  public async deserialize(stepData: any, data: any): Promise<void> {
    // We get the data from the flowCore and we set the values of the inputs
    const entireEventData = data
    ConsoleLog(true, 'entireEventData : ', entireEventData)

    // we check if we are modifying an existing event by checking if the eventData is not empty
    this.modifyingExistingEvent = !isObjectEmpty(entireEventData?.createEventSearchOrAdd?.eventData)
    this.existingRepresentations =
      entireEventData?.createEventSearchOrAdd?.existingRepresentations || []

    // if we are indeed modifying an existing event, we get the data from the eventData and we set the values of the inputs
    if (this.modifyingExistingEvent) {
      if (entireEventData?.createEventSearchOrAdd?.eventData?.createEventAddImage?.imageUrl) {
        this.eventImagePath = entireEventData?.createEventSearchOrAdd.eventData?.imageUrl
        this.eventImage = await MainModule.getInstance()
          .getAuth()
          .getImageUrl(entireEventData.createEventSearchOrAdd.eventData.imageUrl)
      }

      const thumbnailImageObject =
        entireEventData?.createEventSearchOrAdd.eventData?.medias?.[0]?.media
      let thumbnailImage = ''
      if (thumbnailImageObject) {
        const mediaObject = JSON.parse(decodeURIComponent(thumbnailImageObject))
        thumbnailImage = mediaObject?.[0]?.url
      }
      if (thumbnailImage) {
        this.eventImagePath = thumbnailImage
        this.eventImage = thumbnailImage
      } else {
        this.eventImagePath = getCardPlaceholder(
          entireEventData?.createEventSearchOrAdd.eventData?.type
        )
        this.eventImage = getCardPlaceholder(
          entireEventData?.createEventSearchOrAdd.eventData?.type
        )
      }
      this.eventName =
        entireEventData?.createEventSearchOrAdd.eventData?.[
          Locale.language === 'fr' ? 'nameFr' : 'nameEn'
        ]
      this.eventDescription =
        entireEventData?.createEventSearchOrAdd.eventData?.[
          Locale.language === 'fr' ? 'descriptionFr' : 'descriptionEn'
        ]
      // this.eventDisciplines = entireEventData?.createEventSearchOrAdd.eventData?.createEventDescription.disciplines;
      this.representations = entireEventData?.addRepresentation?.representations
    }
    // if we are creating an entirely new event, we get the data from the different steps and we set the values of the inputs
    else {
      this.eventImagePath = entireEventData?.createEventAddImage.imageUrl
      this.eventImage = await MainModule.getInstance()
        .getAuth()
        .getImageUrl(entireEventData.createEventAddImage.imageUrl)
      this.eventName =
        entireEventData?.createEventDescription[
          Locale.language === 'fr' ? 'eventNameFr' : 'eventNameEn'
        ]
      this.eventDescription =
        entireEventData?.createEventDescription[
          Locale.language === 'fr' ? 'eventDescriptionFr' : 'eventDescriptionEn'
        ]
      this.eventDisciplines = entireEventData?.createEventDescription.disciplines
      this.representations = entireEventData?.addRepresentation?.representations
    }

    if (this.flowStep === 'eventFirstRecap' && this.dataType === 'festival') {
      this.representations = entireEventData?.selectedOffers || []
    }

    this.eventFinalRecapData = {
      image: this.eventImage,
      imagePath: this.eventImagePath,
      eventName: this.eventName,
      eventDescription: this.eventDescription,
      eventDisciplines: this.eventDisciplines,
    }

    ConsoleLog(true, 'recap data : ', this.eventFinalRecapData)
    this.rerender()
  }

  renderRepresentation() {
    if (!this.representations || !this.existingRepresentations) {
      return ''
    }
    if (
      this.modifyingExistingEvent ||
      this.representations.length ||
      this.existingRepresentations.length
    ) {
      ConsoleLog(true, 'this.representations when rendering block : ', this.representations)
      return `
          <lvc-box
            display="flex"
            flexDirection="column"
            rowGap="32px"
          >
            <lvc-box>
              ${
                this.modifyingExistingEvent
                  ? `
                <lvc-text font-preset="medium">
                  ${Locale.getKey(
                    this.flowStep === 'eventFirstRecap' && this.dataType === 'festival'
                      ? 'lvc-create-event-representations.existingRepresentationsAndExhibitionsLabel'
                      : 'lvc-create-event-representations.existingRepresentationsLabel'
                  )}
                </lvc-text>
                ${
                  this.existingRepresentations.length
                    ? ''
                    : `
                  <lvc-text font-preset="small">
                    ${Locale.getKey('lvc-create-event-representations.noExistingRepresentationsLabel')}
                  </lvc-text>
                `
                }
              `
                  : ''
              }
              ${this.existingRepresentations
                .map((representation: any, index: number) => {
                  return `
                  <lvc-representation-block
                    representation-data="${encodeURIComponent(JSON.stringify(representation))}"
                    representation-index="${index}"
                    flow-group="${this.flowGroup}"
                    is-existing="true"
                    show-type="${this.flowGroup === 'festivalCreation'}"
                  ></lvc-representation-block>
                `
                })
                .join('')}
            </lvc-box>
            <lvc-box>
              ${
                this.representations?.length
                  ? `
                <lvc-text font-preset="medium">
                  ${Locale.getKey(
                    this.flowStep === 'eventFirstRecap' && this.dataType === 'festival'
                      ? 'lvc-create-event-representations.addedRepresentationsAndExhibitionsLabel'
                      : 'lvc-create-event-representations.addedRepresentationsLabel'
                  )}
                </lvc-text>
              `
                  : ''
              }
              ${
                this.representations?.length
                  ? this.representations
                      ?.map((representation: any, index: number) => {
                        return `
                  <lvc-representation-block
                    representation-data="${encodeURIComponent(JSON.stringify(representation))}"
                    representation-index="${index}"
                    flow-group="${this.flowGroup}"
                    is-existing=${this.flowGroup === 'festivalCreation' ? 'true' : 'false'}
                    show-type="${this.flowGroup === 'festivalCreation'}"
                  ></lvc-representation-block>
                `
                      })
                      .join('')
                  : ''
              }
            </lvc-box>
          </lvc-box>
        `
    }
    return ''
  }

  public async serialize(): Promise<unknown> {
    return ''
    /*     return {
          eventName: this.eventName,
          eventImage: URL.createObjectURL(this.image as File),
          eventDescription: this.eventDescription,
          eventDisciplines: this.eventDisciplines,
        } as LvcCreateEventFinalRecapData; */
  }

  public async validate(): Promise<boolean> {
    const data = this.core?.getData()
    if (
      this.flowStep === 'eventFirstRecap' &&
      this.dataType === 'festival' &&
      !data.addedEventConfirmation
    ) {
      const data = this.core?.getData()
      if (!data.selectedOffers?.length) {
        MainModule.getInstance().showToastAlert(
          Locale.getKey('lvc-create-event-representations.noSelectedOffersError'),
          3000,
          true
        )
        return false
      }
    }
    return true
  }

  onContributorsButtonClick(): void {
    // go to contributors step
    this?.core?.goTo('addEventContributors')
  }

  onAddButtonClickRepresentation(): void {
    // go to add representation step
    this?.core?.goTo('addRepresentation')
  }

  html = () =>
    `<div class="container">


    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="titleMobileSmall">
        ${Locale.getKey(
          this.flowStep === 'eventFirstRecap' && this.dataType === 'festival'
            ? 'lvc-create-event-representations.titleFestival'
            : 'lvc-create-event-representations.title'
        )}
      </lvc-text>
    </lvc-box>

    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="medium">
        ${Locale.getKey(
          this.flowStep === 'eventFirstRecap' && this.dataType === 'festival'
            ? 'lvc-create-event-representations.descriptionFestival'
            : 'lvc-create-event-representations.description'
        )}
      </lvc-text>
    </lvc-box>

    <lvc-box mt="24px">
    ${this.renderRepresentation()}
    </lvc-box>
    ${
      this.dataType === 'festival'
        ? `
        <lvc-box mt="24px">
          <lvc-event-search-field
            title="${Locale.getKey('lvc-create-event-representations.nameOfEventOrExhibition')}"
            placeholder="${Locale.getKey('lvc-create-event-representations.nameOfEventOrExhibition')}"
            on-change="${this.onEventChange}"
            events-type="events-and-representation"
            disable-creation="true"
          ></lvc-event-search-field>
        </lvc-box>
      `
        : `
        <lvc-box mt="24px" mb="24px" display="flex" justifyContent="center" flexDirection="column">
          <lvc-button theme="transparent" height="40px" border="1px solid #1E007D" width="100%" name="addRepresentation" display="flex" justify-content="center" align-items="center">
            <lvc-add-icon data-color="#1E007D"></lvc-add-icon>
            ${Locale.getKey('lvc-create-event-representations.addRepresentation')}
          </lvc-button>
        </lvc-box>
      `
    }
  

  </div>
    `

  eventListeners: EventListeners = [
    ['lvc-event-search-field', 'change', this.handleModalEvent.bind(this)],
    [
      'lvc-creation-recap',
      'lvc-event-recap:goToContributors',
      this.onContributorsButtonClick.bind(this),
    ],
    ["[name='addRepresentation']", 'click', this.onAddButtonClickRepresentation.bind(this)],
  ]

  css = () => {
    return /*css*/ `

  .footer {
    display: flex;
    height: 60px;
    padding: 0 10px;
  }

  lvc-add-icon{
    display: flex;
    justify-content: center;
    margin-right: 8px;
  }

  .addContributor {
    color: #ffff;
    background: var(--Minuit, #1E007D);
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%; 
    display: flex;
    padding: 10px 16px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex: 1 1 0;
    border: none;
    margin: 10px;
    cursor: pointer;
    border-radius: 999px;
    `
  }
}
